<!--
@component
Round chip that explains itself in a tooltip: it owns the chip shape, the tone colours and the
tooltip frame, while the glyph and everything below the title come in as snippets.

`name` lands as `data-chip` on both the chip and the tooltip body. Every chip on a page renders
its body into the same document, so that attribute is the only thing telling the bodies apart.
-->
<script module lang="ts">
	/** what the chip means, named after the role and not the colour so a tone can be re-skinned */
	export enum ChipTone {
		/** neutral information */
		INFO = 'INFO',
		/** something that no longer carries weight */
		MUTED = 'MUTED',
		/** something that counts */
		POSITIVE = 'POSITIVE',
		/** something that has to be acted on */
		DANGER = 'DANGER'
	}
</script>

<script lang="ts">
	import Tooltip_v5 from '$lib/atoms/Tooltip_v5.svelte'
	import { TooltipPlacements } from '$types/enums'
	import type { Snippet } from 'svelte'

	interface Props {
		/** what the chip stands for, exposed as `data-chip` */
		name: string
		tone: ChipTone
		/** headline above the body, left out when the body speaks for itself */
		title?: string
		placement?: TooltipPlacements
		/** the glyph, wrapped by the caller when it needs a nudge or a stroke weight of its own */
		icon: Snippet
		/** the tooltip body below the title, laid out as a column by this component */
		children: Snippet
	}

	let {
		name,
		tone,
		title = '',
		placement = TooltipPlacements.RIGHT,
		icon,
		children
	}: Props = $props()
</script>

<Tooltip_v5 placementTtip={placement} appendToObject="parent">
	<div class="tooltip-chip" data-chip={name} data-tone={tone}>
		{@render icon()}
	</div>
	{#snippet content()}
		<div class="tooltip-chip-body" data-chip={name}>
			{#if title !== ''}
				<span class="tooltip-chip-title">{title}</span>
			{/if}
			{@render children()}
		</div>
	{/snippet}
</Tooltip_v5>

<style>
	.tooltip-chip {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex: none;
		width: 2rem;
		height: 2rem;
		border-radius: 50%;

		&[data-tone='INFO'] {
			background-color: var(--color-blue-100);
			color: var(--color-blue-500);
		}

		&[data-tone='MUTED'] {
			background-color: var(--color-gray-100);
			color: var(--color-gray-500);
		}

		&[data-tone='POSITIVE'] {
			background-color: var(--color-green-100);
			color: var(--color-green-500);
		}

		&[data-tone='DANGER'] {
			background-color: var(--color-highlight-red-200);
			color: var(--color-highlight-red-800);
		}
	}

	.tooltip-chip-body {
		display: flex;
		flex-direction: column;
		gap: 4px;
		width: 100%;
		text-align: left;
	}

	.tooltip-chip-title {
		font-weight: 500;
	}
</style>
